import { Button, Field, Input, Notice } from '@didto/ui';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { errorText, isNetworkError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import { AuthFrame } from './AuthFrame';
import './auth.css';

/** TOTP step-up for logins that have a verified authenticator factor. */
export function MfaPage() {
  const s = useSession();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  // ADR-037: the code is checked by the server; the connection banner above says why it is paused
  const online = useOnline();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.mfa
      .listFactors()
      .then(({ data, error: err }) => {
        if (err) throw err;
        const f = data?.totp.find((x) => x.status === 'verified');
        setFactorId(f?.id ?? null);
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.trim(),
      });
      if (err) {
        // a dropped connection is not a wrong code
        setError(
          isNetworkError(err)
            ? errorText(err)
            : 'That code was not accepted. Codes rotate every 30 seconds.',
        );
        return;
      }
      await s.refresh();
      nav(loc.state?.from ?? '/', { replace: true });
    } catch (thrown) {
      setError(errorText(thrown));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Authenticator code" subtitle="Two-step verification is on for this login">
      <form onSubmit={(e) => void submit(e)} className="d-stack">
        <Field label="6-digit code" help="From your authenticator app">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="signin__code"
              data-testid="mfa-code"
            />
          )}
        </Field>
        {error && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online || !factorId || code.length < 6}
        >
          Verify
        </Button>
        <Button variant="quiet" onClick={() => void s.signOut().then(() => nav('/login'))}>
          Cancel and sign out
        </Button>
      </form>
    </AuthFrame>
  );
}
